import { ExternalLink, FileText, Loader2 } from 'lucide-react';

import { useExamesControllerUrlDoAnexo } from '@/api/generated/exames/exames';
import type { AnexoDoExameResponse } from '@/api/generated/model';
import { EstadoErro } from '@/shared/components/estados';
import { Button } from '@/shared/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

interface Props {
  exameId: string;
  /** `null` fecha. */
  anexo: AnexoDoExameResponse | null;
  aoFechar: () => void;
}

/**
 * Abre um documento de exame externo — o laudo em PDF ou a foto do papel.
 *
 * ⚠️ A URL é **assinada e curta**, buscada aqui e não embutida na listagem:
 * assinar todo anexo de toda página custaria uma ida ao Storage por documento
 * que ninguém abriu. `staleTime` curto pelo mesmo motivo inverso — reabrir o
 * mesmo anexo em seguida não precisa de assinatura nova.
 *
 * ⚠️ Não reusa o `LaudoEspecialDialogo`: lá a URL é uma rota da própria API
 * (same-origin, com cookie), aqui é um link assinado do Storage. O `download`
 * do `<a>` não vale cross-origin, então o botão diz "abrir em nova aba", que é
 * o que de fato acontece.
 */
export function AnexoDoExameDialogo({ exameId, anexo, aoFechar }: Props) {
  const { data, isPending, isError, refetch } = useExamesControllerUrlDoAnexo(
    exameId,
    anexo?.id ?? '',
    { query: { enabled: Boolean(anexo), staleTime: 60_000, retry: false } },
  );

  const ehImagem = data?.mimeType.startsWith('image/') ?? false;

  return (
    <Dialog open={anexo !== null} onOpenChange={(aberto) => !aberto && aoFechar()}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <FileText className="w-4 h-4 text-brand shrink-0" />
            <span className="truncate">{anexo?.nomeOriginal ?? 'Documento'}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 rounded-md bg-muted/30 overflow-hidden flex items-center justify-center">
          {isPending ? (
            <Loader2 className="w-6 h-6 animate-spin text-brand" aria-label="Carregando" />
          ) : isError || !data ? (
            <EstadoErro
              titulo="Não foi possível abrir o documento"
              descricao="Ele pode ter sido removido."
              aoTentarDeNovo={() => void refetch()}
            />
          ) : ehImagem ? (
            <img
              src={data.url}
              alt={data.nomeOriginal}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <iframe
              key={data.url}
              src={data.url}
              title={data.nomeOriginal}
              className="w-full h-full border-0"
            />
          )}
        </div>

        <div className="flex justify-end gap-2 shrink-0">
          {data && (
            <Button asChild variant="outline">
              <a href={data.url} target="_blank" rel="noreferrer">
                <ExternalLink className="w-4 h-4" />
                Abrir em nova aba
              </a>
            </Button>
          )}
          <Button variant="ghost" onClick={aoFechar}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
